import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Add as AddIcon,
  Inbox as InboxIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";

import useApi from "../../hooks/useApi";
import Page from "./Page";
import Loading from "../ui/Loading";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import AlertDialog from "../ui/AlertDialog";

const Messages = () => {
  const navigate = useNavigate();
  const { postData } = useApi();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: "",
    message: "",
    severity: "info",
    onConfirm: null,
  });

  const toggleRow = (messageId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    const response = await postData({
      entityName: "Message",
      action: "getMessageList",
    });
    if (response.isSuccessful) {
      setMessages(response.data);
    }
    setLoading(false);
  };

  const handleCreateMessage = () => {
    navigate("/createmessage");
  };

  const handleViewInbox = () => {
    navigate("/messageinbox");
  };

  const handleViewThread = (messageId) => {
    navigate(`/messagethread/${messageId}`);
  };

  const handleEditMessage = (messageId) => {
    navigate(`/editmessage/${messageId}`);
  };

  const handleDeleteMessage = (messageId) => {
    setMessageToDelete(messageId);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    setOpenDialog(false);

    if (!messageToDelete) return;

    const response = await postData({
      entityName: "Message",
      action: "deleteMessage",
      data: { messageId: messageToDelete },
    });

    if (response.isSuccessful) {
      loadMessages();
    } else {
      setAlertDialog({
        open: true,
        title: "Error",
        message: response.message || "Failed to delete message",
        severity: "error",
        onConfirm: null,
      });
    }

    setMessageToDelete(null);
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
    setMessageToDelete(null);
  };

  const handlePublishMessage = async (messageId) => {
    const response = await postData({
      entityName: "Message",
      action: "publishMessage",
      data: { messageId },
    });

    if (response.isSuccessful) {
      loadMessages();
    } else {
      setAlertDialog({
        open: true,
        title: "Error",
        message:
          response.message || "Failed to publish message (check targeting)",
        severity: "error",
        onConfirm: null,
      });
    }
  };

  const handleCancelMessage = async (messageId) => {
    const response = await postData({
      entityName: "Message",
      action: "cancelMessage",
      data: { messageId },
    });

    if (response.isSuccessful) {
      loadMessages();
    } else {
      setAlertDialog({
        open: true,
        title: "Error",
        message: response.message || "Failed to cancel message",
        severity: "error",
        onConfirm: null,
      });
    }
  };

  const handleAlertDialogClose = () => {
    setAlertDialog({ ...alertDialog, open: false });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent":
        return "error";
      case "high":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "published":
        return "success";
      case "pending":
        return "default";
      case "cancelled":
        return "warning";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTargetingDisplay = (message) => {
    const targets = [];
    if (message.userId) targets.push("User");
    if (message.clientId) targets.push("Client");
    if (message.projectId) targets.push("Project");
    if (message.province) targets.push(`Province: ${message.province}`);
    if (message.bannerId) targets.push("Banner");
    if (message.retailerId) targets.push("Retailer");
    return targets.length > 0 ? targets.join(", ") : "All";
  };

  if (loading) {
    return (
      <Page
        pageName="messages"
        backPath="/communication"
      >
        <Loading />
      </Page>
    );
  }

  return (
    <>
      <AlertDialog
        open={alertDialog.open}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
        onClose={handleAlertDialogClose}
        onConfirm={alertDialog.onConfirm}
      />
      <Page
        pageName="messages"
        backPath="/communication"
      >
        <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateMessage}
          >
            Create Message
          </Button>
          <Button
            variant="outlined"
            startIcon={<InboxIcon />}
            onClick={handleViewInbox}
          >
            View Replies Inbox
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Message</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="center"
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      No messages found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((message) => (
                  <>
                    <TableRow
                      key={message._id}
                      hover
                    >
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(message._id)}
                        >
                          {expandedRows[message._id] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold" }}
                          >
                            {message.title}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mt: 0.5,
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label={message.priority}
                              color={getPriorityColor(message.priority)}
                              size="small"
                            />
                            <Chip
                              label={message.status || "pending"}
                              color={getStatusColor(message.status)}
                              size="small"
                              variant="outlined"
                            />
                            {message.unreadReplyCount > 0 && (
                              <Chip
                                label={`${message.unreadReplyCount} unread`}
                                color="primary"
                                size="small"
                              />
                            )}
                            {message.requiresAcknowledgement && (
                              <Chip
                                label="Requires ACK"
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {message.allowReplies && (
                              <Chip
                                label="Replies Allowed"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            justifyContent: "center",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleViewThread(message._id)}
                            title="View Thread"
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEditMessage(message._id)}
                            disabled={message.status !== "pending"}
                            title="Edit Message"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handlePublishMessage(message._id)}
                            disabled={message.status !== "pending"}
                            title="Publish Message"
                          >
                            <AddIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleCancelMessage(message._id)}
                            disabled={
                              message.status === "expired" ||
                              message.status === "cancelled"
                            }
                            title="Cancel Message"
                          >
                            <CancelIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteMessage(message._id)}
                            disabled={
                              message.status === "published" ||
                              message.status === "expired"
                            }
                            title="Delete Message"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={3}
                      >
                        <Collapse
                          in={expandedRows[message._id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ margin: 2 }}>
                            <Typography
                              variant="h6"
                              gutterBottom
                              component="div"
                            >
                              Message Details
                            </Typography>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "repeat(auto-fit, minmax(250px, 1fr))",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Category
                                </Typography>
                                <Typography variant="body1">
                                  {message.category || "N/A"}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Targeting
                                </Typography>
                                <Typography variant="body1">
                                  {getTargetingDisplay(message)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Recipients
                                </Typography>
                                <Typography variant="body1">
                                  {message.recipientCount}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Replies
                                </Typography>
                                <Typography variant="body1">
                                  {message.replyCount}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Created
                                </Typography>
                                <Typography variant="body1">
                                  {formatDate(message.createdAt)}
                                </Typography>
                              </Box>
                              {message.publishedAt && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Published
                                  </Typography>
                                  <Typography variant="body1">
                                    {formatDate(message.publishedAt)}
                                  </Typography>
                                </Box>
                              )}
                              <Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Created By
                                </Typography>
                                <Typography variant="body1">
                                  {message.createdByName}
                                </Typography>
                              </Box>
                              {message.expiresAt && (
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Expires
                                  </Typography>
                                  <Typography variant="body1">
                                    {formatDate(message.expiresAt)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {openDialog && (
          <Dialog_YesNo
            open={openDialog}
            onClose={handleCancelDelete}
            title="Delete Message"
            message="Are you sure you want to delete this message and all its replies? This action cannot be undone."
            onYes={handleConfirmDelete}
            onNo={handleCancelDelete}
          />
        )}
      </Page>
    </>
  );
};

export default Messages;
